/** @sfdc-extension-file SFDC_EXT_BRD_LOGIN_SIDEBAR */
import { useCallback, useEffect } from 'react';
import { useFetcher } from 'react-router';
import { LOYALTY_CONTENT_FETCHER_KEY, LOYALTY_CONTENT_RESOURCE } from '@/extensions/brd-login-sidebar/constants';
import type { LoyaltyContentResult } from '@/extensions/brd-login-sidebar/routes/resource.loyalty-content';

/**
 * Shared loyalty CMS content for the auth decision step.
 *
 * Uses a named fetcher so a header hover/focus prefetch and the decision step
 * share one load — ideally already in memory when the sidebar opens.
 */
export function useLoyaltyContent(): {
    prefetch: () => void;
    loyaltyContent: string | undefined;
} {
    const { load, state, data } = useFetcher<LoyaltyContentResult>({
        key: LOYALTY_CONTENT_FETCHER_KEY,
    });

    const prefetch = useCallback(() => {
        if (state !== 'idle' || data != null) {
            return;
        }
        void load(LOYALTY_CONTENT_RESOURCE);
    }, [load, state, data]);

    return {
        prefetch,
        loyaltyContent: data?.success ? data.content : undefined,
    };
}

/**
 * Kick off {@link useLoyaltyContent} prefetch once when `enabled` becomes true
 * (e.g. header hover/focus intent — not on every guest page mount).
 */
export function useLoyaltyContentPrefetch(enabled = true): void {
    const { prefetch } = useLoyaltyContent();

    useEffect(() => {
        if (!enabled) {
            return;
        }
        prefetch();
    }, [enabled, prefetch]);
}
